import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ArrowLeft, Plus, Upload, Trash2, Loader2, Image, ClipboardCheck, DollarSign } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCarForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [availableExtras, setAvailableExtras] = useState([]);
  
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    mileage: 0,
    fuel: 'Gasóleo',
    transmission: 'Manual',
    price: '',
    description: '',
    youtube_url: '',
    status: 'Rascunho',
    extras: [],
    images: [],
    featured: false,
    promotion: '',
    last_inspection_date: '',
    next_inspection_date: '',
    purchase_price: '',
    sale_price: ''
  });

  useEffect(() => {
    fetchExtras();
    if (isEditing) {
      fetchCar();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExtras = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/extras`);
      setAvailableExtras(response.data);
    } catch (error) {
      console.error('Error fetching extras:', error);
    }
  };

  const fetchCar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar viatura');
      navigate('/admin/viaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumericChange = (field, value, fallback = '') => {
    if (value === '' || value === undefined || value === null) {
      setFormData(prev => ({ ...prev, [field]: fallback }));
      return;
    }
    const num = parseFloat(value);
    setFormData(prev => ({ ...prev, [field]: isNaN(num) ? fallback : num }));
  };

  const handleExtraToggle = (extra) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.includes(extra)
        ? prev.extras.filter(e => e !== extra)
        : [...prev.extras, extra]
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const uploadedUrls = [];

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} não é uma imagem válida`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} é demasiado grande (máx 10MB)`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(
          `${API_URL}/api/upload/image`,
          formDataUpload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Store relative URL path (not full URL) so it works across environments
        const imageUrl = response.data.url;
        uploadedUrls.push(imageUrl);
        toast.success(`${file.name} carregada com sucesso`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erro ao carregar ${file.name}`);
      }
    }

    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    }

    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUrlAdd = () => {
    const url = prompt('Cole o URL da imagem:');
    if (url && url.trim()) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url.trim()]
      }));
    }
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model) {
      toast.error('Por favor preencha a marca e o modelo');
      return;
    }

    const price = parseFloat(formData.price);
    if (!price || price <= 0) {
      toast.error('O preço anunciado é obrigatório e deve ser maior que zero');
      return;
    }

    const purchasePrice = parseFloat(formData.purchase_price);
    if (!purchasePrice || purchasePrice <= 0) {
      toast.error('O preço de compra é obrigatório para registar a viatura');
      return;
    }

    if (formData.status === 'Vendido') {
      const salePrice = parseFloat(formData.sale_price);
      if (!salePrice || salePrice <= 0) {
        toast.error('Defina o valor real de venda antes de marcar como vendida');
        return;
      }
    }

    // Prepare data with proper numeric values
    const submitData = {
      ...formData,
      price: price,
      purchase_price: purchasePrice,
      sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
      year: parseInt(formData.year) || new Date().getFullYear(),
      mileage: parseInt(formData.mileage) || 0,
    };

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (isEditing) {
        await axios.put(`${API_URL}/api/cars/${id}`, submitData, config);
        toast.success('Viatura atualizada com sucesso');
      } else {
        await axios.post(`${API_URL}/api/cars`, submitData, config);
        toast.success('Viatura criada com sucesso');
      }
      navigate('/admin/viaturas');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Erro ao guardar viatura';
      if (typeof detail === 'string') message = detail;
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const fuelOptions = ['Gasóleo', 'Gasolina', 'Diesel', 'Híbrido', 'Elétrico'];
  const transmissionOptions = ['Manual', 'Automática'];
  const statusOptions = ['Rascunho', 'Disponível', 'Reservado', 'Vendido'];

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-zinc-400">A carregar...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/viaturas')}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-medium text-white" data-testid="form-title">
              {isEditing ? 'Editar Viatura' : 'Nova Viatura'}
            </h1>
            <p className="text-zinc-500">
              {isEditing ? 'Atualize os dados da viatura' : 'Preencha os dados da nova viatura'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <h3 className="text-lg font-medium text-white mb-6">Informações Básicas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Marca *</label>
                <Input
                  value={formData.brand}
                  onChange={(e) => handleChange('brand', e.target.value)}
                  placeholder="Ex: BMW"
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-brand-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Modelo *</label>
                <Input
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  placeholder="Ex: 318d Touring"
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-model-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Ano</label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleNumericChange('year', e.target.value, new Date().getFullYear())}
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-year-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Quilómetros</label>
                <Input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => handleNumericChange('mileage', e.target.value, 0)}
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-mileage-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Combustível</label>
                <Select value={formData.fuel} onValueChange={(v) => handleChange('fuel', v)}>
                  <SelectTrigger className="bg-[#090A0F] border-zinc-800 text-white" data-testid="car-fuel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141D] border-zinc-800">
                    {fuelOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-zinc-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Caixa</label>
                <Select value={formData.transmission} onValueChange={(v) => handleChange('transmission', v)}>
                  <SelectTrigger className="bg-[#090A0F] border-zinc-800 text-white" data-testid="car-transmission-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141D] border-zinc-800">
                    {transmissionOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-zinc-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Estado</label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger className="bg-[#090A0F] border-zinc-800 text-white" data-testid="car-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141D] border-zinc-800">
                    {statusOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-zinc-800">{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Featured/Destaque */}
              <div className="flex items-center gap-3 pt-6">
                <Checkbox
                  checked={formData.featured}
                  onCheckedChange={(checked) => handleChange('featured', checked)}
                  className="border-zinc-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 w-5 h-5"
                  data-testid="car-featured-checkbox"
                />
                <label className="text-white font-medium cursor-pointer" onClick={() => handleChange('featured', !formData.featured)}>
                  ⭐ Destacar como Oportunidade
                </label>
              </div>
              {/* Promotion */}
              <div className="pt-4">
                <label className="text-zinc-400 text-sm mb-2 block">Promoção (badge triangular)</label>
                <Input
                  value={formData.promotion || ''}
                  onChange={(e) => handleChange('promotion', e.target.value || null)}
                  placeholder="Ex: -10%, PROMOÇÃO, NOVO"
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-promotion-input"
                />
                <p className="text-zinc-500 text-xs mt-1">Deixe vazio para não mostrar badge</p>
              </div>
            </div>
          </div>

          {/* Financial / Financeiro */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
              <DollarSign size={20} className="text-red-500" />
              Dados Financeiros
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Preço de Compra (EUR) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.purchase_price || ''}
                  onChange={(e) => handleNumericChange('purchase_price', e.target.value, '')}
                  placeholder="Quanto pagou pela viatura"
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-purchase-price-input"
                />
                <p className="text-zinc-500 text-xs mt-1">Obrigatório para registar a viatura</p>
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Preço Anunciado (EUR) *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price || ''}
                  onChange={(e) => handleNumericChange('price', e.target.value, '')}
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-price-input"
                />
                <p className="text-zinc-500 text-xs mt-1">Preço visível no website</p>
              </div>
              {formData.status === 'Vendido' && (
                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Valor Real de Venda (EUR) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sale_price || ''}
                    onChange={(e) => handleNumericChange('sale_price', e.target.value, '')}
                    placeholder="Valor final de venda"
                    className="bg-[#090A0F] border-red-600/50 text-white ring-1 ring-red-600/30"
                    data-testid="car-sale-price-input"
                  />
                  <p className="text-red-400 text-xs mt-1">Obrigatório ao marcar como vendida</p>
                </div>
              )}
            </div>
            {parseFloat(formData.purchase_price) > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-6 text-sm">
                <div>
                  <span className="text-zinc-500">Custo: </span>
                  <span className="text-white font-medium">{new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(parseFloat(formData.purchase_price) || 0)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Margem estimada: </span>
                  <span className={`font-medium ${((parseFloat(formData.price) || 0) - (parseFloat(formData.purchase_price) || 0)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format((parseFloat(formData.price) || 0) - (parseFloat(formData.purchase_price) || 0))}
                  </span>
                </div>
                {formData.status === 'Vendido' && parseFloat(formData.sale_price) > 0 && (
                  <div>
                    <span className="text-zinc-500">Lucro real: </span>
                    <span className={`font-semibold ${((parseFloat(formData.sale_price) || 0) - (parseFloat(formData.purchase_price) || 0)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format((parseFloat(formData.sale_price) || 0) - (parseFloat(formData.purchase_price) || 0))}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description & Video */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <h3 className="text-lg font-medium text-white mb-6">Descrição e Vídeo</h3>
            <div className="space-y-6">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Descrição</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Descreva a viatura em detalhe..."
                  rows={6}
                  className="bg-[#090A0F] border-zinc-800 text-white resize-none"
                  data-testid="car-description-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">URL do Vídeo YouTube</label>
                <Input
                  value={formData.youtube_url}
                  onChange={(e) => handleChange('youtube_url', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-youtube-input"
                />
              </div>
            </div>
          </div>

          {/* Inspection / Inspeção */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <h3 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
              <ClipboardCheck size={20} className="text-red-500" />
              Inspeção
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Última Inspeção</label>
                <Input
                  type="date"
                  value={formData.last_inspection_date || ''}
                  onChange={(e) => handleChange('last_inspection_date', e.target.value || null)}
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-last-inspection-input"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Próxima Inspeção</label>
                <Input
                  type="date"
                  value={formData.next_inspection_date || ''}
                  onChange={(e) => handleChange('next_inspection_date', e.target.value || null)}
                  className="bg-[#090A0F] border-zinc-800 text-white"
                  data-testid="car-next-inspection-input"
                />
                <p className="text-zinc-500 text-xs mt-1">Receberá um alerta 15 dias antes da data</p>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-white">Imagens</h3>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  data-testid="image-upload-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="upload-image-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      A carregar...
                    </>
                  ) : (
                    <>
                      <Upload size={18} className="mr-2" />
                      Carregar Imagens
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleImageUrlAdd}
                  className="bg-zinc-800 text-white hover:bg-zinc-700"
                  data-testid="add-image-url-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Adicionar URL
                </Button>
              </div>
            </div>
            
            {formData.images.length === 0 ? (
              <div 
                className="border-2 border-dashed border-zinc-800 p-12 text-center cursor-pointer hover:border-zinc-600 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Image size={48} className="mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">Clique ou arraste imagens para carregar</p>
                <p className="text-zinc-600 text-sm mt-1">JPG, PNG, GIF ou WebP até 10MB</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="images-grid">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative aspect-video bg-zinc-800 overflow-hidden group">
                    <img src={img} alt={`Imagem ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleImageRemove(index)}
                      className="absolute top-2 right-2 p-2 bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-image-${index}`}
                    >
                      <Trash2 size={16} />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-2 left-2 text-xs bg-red-600 text-white px-2 py-1 font-semibold">
                        Principal
                      </span>
                    )}
                  </div>
                ))}
                {/* Add more button */}
                <div 
                  className="aspect-video border-2 border-dashed border-zinc-800 flex items-center justify-center cursor-pointer hover:border-zinc-600 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="text-center">
                    <Plus size={32} className="mx-auto text-zinc-600 mb-2" />
                    <p className="text-zinc-500 text-sm">Adicionar mais</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Extras */}
          <div className="bg-[#12141D] border border-zinc-800 p-6">
            <h3 className="text-lg font-medium text-white mb-6">Equipamentos</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="extras-grid">
              {availableExtras.map(extra => (
                <label 
                  key={extra} 
                  className="flex items-center gap-3 p-3 bg-[#090A0F] border border-zinc-800 hover:border-zinc-600 cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={formData.extras.includes(extra)}
                    onCheckedChange={() => handleExtraToggle(extra)}
                    className="border-zinc-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                    data-testid={`extra-${extra.toLowerCase().replace(/\s/g, '-')}`}
                  />
                  <span className="text-zinc-300 text-sm">{extra}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              onClick={() => navigate('/admin/viaturas')}
              className="bg-zinc-800 text-white hover:bg-zinc-700 px-8"
              data-testid="cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-red-600 text-white hover:bg-red-700 px-8 font-semibold"
              data-testid="save-car-btn"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'A guardar...' : (isEditing ? 'Atualizar' : 'Criar Viatura')}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminCarForm;
